import React, { useState } from 'react';
import { Modal, Form, Input, Select, message, Typography, Collapse, Space } from 'antd';
import { BugOutlined, BulbOutlined, MessageOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { feedbackApi } from '../../api/client';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

interface FloatingFeedbackProps {
    isOpen: boolean;
    onClose: () => void;
}

const FloatingFeedback: React.FC<FloatingFeedbackProps> = ({ isOpen, onClose }) => {
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const [feedbackType, setFeedbackType] = useState<string>('BUG');

    const createFeedbackMutation = useMutation({
        mutationFn: (values: any) => feedbackApi.create({
            ...values,
            pageUrl: window.location.href,
            metadata: {
                userAgent: navigator.userAgent,
                screenSize: `${window.innerWidth}x${window.innerHeight}`,
                browser: navigator.vendor,
                os: navigator.platform,
            }
        }),
        onSuccess: () => {
            message.success('Dziękujemy za zgłoszenie! Zajmiemy się tym.');
            onClose();
            form.resetFields();
            setFeedbackType('BUG');
            queryClient.invalidateQueries({ queryKey: ['feedback-me'] });
        },
        onError: () => {
            message.error('Wystąpił błąd podczas wysyłania zgłoszenia.');
        }
    });

    const handleSubmit = (values: any) => {
        createFeedbackMutation.mutate(values);
    };

    const handleTypeChange = (value: string) => {
        setFeedbackType(value);
        if (value !== 'BUG') {
            form.setFieldsValue({
                stepsToReproduce: undefined,
                expectedBehavior: undefined,
                actualBehavior: undefined,
            });
        }
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MessageOutlined />
                    <span>Zgłoś błąd lub zaproponuj ulepszenie</span>
                </div>
            }
            open={isOpen}
            onCancel={onClose}
            onOk={() => form.submit()}
            confirmLoading={createFeedbackMutation.isPending}
            okText="Wyślij zgłoszenie"
            cancelText="Anuluj"
            width={600}
        >
            <div style={{ marginBottom: 16 }}>
                <Text type="secondary">
                    Twoja opinia pomaga nam ulepszać CureCraft. Automatycznie zapiszemy stronę, na której aktualnie jesteś.
                </Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ type: 'BUG' }}>
                <Form.Item
                    name="type"
                    label="Typ zgłoszenia"
                    rules={[{ required: true }]}
                >
                    <Select
                        onChange={handleTypeChange}
                        options={[
                            { label: <Space><BugOutlined style={{ color: '#ff4d4f' }} /> Błąd (coś nie działa)</Space>, value: 'BUG' },
                            { label: <Space><BulbOutlined style={{ color: '#faad14' }} /> Pomysł / Ulepszenie</Space>, value: 'FEATURE_REQUEST' },
                            { label: <Space><ThunderboltOutlined style={{ color: '#52c41a' }} /> Inne</Space>, value: 'OTHER' },
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    name="title"
                    label="Krótki tytuł"
                    rules={[{ required: true, message: 'Podaj krótki tytuł zgłoszenia' }, { max: 200 }]}
                >
                    <Input placeholder="np. Nie mogę pobrać faktury PDF" />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Opis"
                    rules={[{ required: true, message: 'Opisz proszę szczegóły' }]}
                >
                    <Input.TextArea
                        rows={4}
                        placeholder={
                            feedbackType === 'BUG'
                                ? 'Opisz dokładnie co się stało...'
                                : 'Opisz swój pomysł lub sugestię...'
                        }
                    />
                </Form.Item>

                {feedbackType === 'BUG' && (
                    <Collapse
                        ghost
                        size="small"
                        style={{ marginBottom: 16 }}
                        items={[{
                            key: '1',
                            label: <Text strong style={{ fontSize: 13 }}>Dodatkowe informacje (opcjonalne dla zgłoszeń błędów)</Text>,
                            children: (
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    <Form.Item
                                        name="stepsToReproduce"
                                        label={<Text style={{ fontSize: 13 }}>Kroki do odtworzenia</Text>}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Input.TextArea
                                            rows={3}
                                            placeholder="1. Przejdź do strony zamówień&#10;2. Kliknij na szczegóły zamówienia&#10;3. Błąd: strona się nie ładuje"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="expectedBehavior"
                                        label={<Text style={{ fontSize: 13 }}>Oczekiwane zachowanie</Text>}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Input.TextArea
                                            rows={2}
                                            placeholder="Strona powinna wyświetlić szczegóły zamówienia z numerem, datą i listą produktów."
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        name="actualBehavior"
                                        label={<Text style={{ fontSize: 13 }}>Faktyczne zachowanie</Text>}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Input.TextArea
                                            rows={2}
                                            placeholder="Strona pozostaje pusta, a w konsoli przeglądarki widać błąd 500."
                                        />
                                    </Form.Item>
                                </Space>
                            )
                        }]}
                    />
                )}
            </Form>
        </Modal>
    );
};

export default FloatingFeedback;